import React from 'react';
import { Modal, Spin } from 'antd';
import { Check, FileSpreadsheet } from 'lucide-react';
import { EXPORT_MAX_ROWS, fmtCount } from '../levantamientoShared';

const STEPS = [
    { key: 'fetching', label: 'Consultar registros' },
    { key: 'building', label: 'Generar el Excel' }
];

/** One step of the export: done (check), active (spinner) or pending (number). */
function StepPill({ index, label, state }) {
    const chip =
        state === 'done'
            ? 'bg-success-bg text-success-fg'
            : state === 'active'
              ? 'bg-primary-50 text-primary-600'
              : 'bg-surface-100 text-surface-400';
    return (
        <div className="flex items-center gap-2">
            <span className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold ${chip}`}>
                {state === 'done' ? <Check /> : state === 'active' ? <Spin size="small" /> : index + 1}
            </span>
            <span className={`text-[13px] ${state === 'pending' ? 'text-surface-400' : 'text-surface-700 font-medium'}`}>{label}</span>
        </div>
    );
}

/**
 * Blocking progress modal for the Levantamiento Excel export: step, record count, cap notice
 * and an elapsed timer, so a long export doesn't look frozen.
 */
export default function ExportProgressModal({ open, step, elapsed = 0, rowCount = 0, total = 0 }) {
    const capped = total > EXPORT_MAX_ROWS;
    const expected = Math.min(total, EXPORT_MAX_ROWS);
    const mins = Math.floor(elapsed / 60);
    const secs = String(elapsed % 60).padStart(2, '0');
    const activeIdx = step === 'building' ? 1 : 0;

    return (
        <Modal open={open} closable={false} maskClosable={false} keyboard={false} footer={null} centered width={440}>
            <div className="flex flex-col items-center text-center gap-3 pt-2">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-xl text-primary-600">
                    <FileSpreadsheet />
                </span>
                <h3 className="m-0 text-[17px] font-semibold text-surface-900">
                    {step === 'building' ? 'Generando el archivo Excel…' : 'Preparando la exportación…'}
                </h3>
                <p className="m-0 text-sm text-surface-600">
                    {step === 'building' ? (
                        <>
                            Armando <b className="tabular-nums text-surface-900">{fmtCount(rowCount)}</b> registros en el Excel.
                        </>
                    ) : (
                        <>
                            Consultando <b className="tabular-nums text-surface-900">{fmtCount(expected)}</b> registros de precios
                            {capped && <> (los más recientes de {fmtCount(total)})</>}.
                        </>
                    )}
                </p>
                {capped && step !== 'building' && (
                    <p className="m-0 rounded-lg border border-warning-border bg-warning-bg px-3 py-2 text-xs text-warning-fg">
                        El máximo por exportación es {fmtCount(EXPORT_MAX_ROWS)} registros. Para el resto, acota las
                        fechas o filtros.
                    </p>
                )}

                <div className="flex w-full items-center justify-center gap-4 rounded-lg border border-highlight-border bg-highlight-bg px-4 py-3">
                    {STEPS.map((s, i) => (
                        <React.Fragment key={s.key}>
                            {i > 0 && <span className="h-px w-6 bg-surface-200" />}
                            <StepPill index={i} label={s.label} state={i < activeIdx ? 'done' : i === activeIdx ? 'active' : 'pending'} />
                        </React.Fragment>
                    ))}
                </div>

                <p className="m-0 text-[13px] text-surface-500">
                    {expected > 10000 ? 'Con este volumen puede tardar uno o dos minutos.' : 'Puede tardar unos segundos.'}{' '}
                    No cierres ni recargues esta pestaña.
                </p>
                <span className="text-xs text-surface-400 tabular-nums">
                    Tiempo transcurrido: {mins}:{secs}
                </span>
            </div>
        </Modal>
    );
}
